class ProductStepper {
  constructor(element) {
    this.element = element;
    this.currentStep = 1;
    this.activeTab = 'patient';
    this.formData = {};
    this.selectedIngredients = new Map();
    this.numberOfDoses = 30;
    this.youtubePlayers = {};
    this.completedSteps = new Set([1]);
    this.PRICING_CONSTANTS = window.STEPPER_PRICING_CONSTANTS || {
      BASE_MARKUP: 2,
      PACKAGING_COST: 10,
      LABOR_COST: 85,
      DOSE_SIZE_GRAMS: 10,
      BASE_POWDER_COST_PER_GRAM: 0.095
    };
    this.variantId = window.STEPPER_PRODUCT_VARIANT_ID;
    
    this.init();
  }

  init() {
    this.setupTabs();
    this.setupStepNavigation();
    this.setupForm();
    this.setupVideo();
    this.setupStep3();
    this.sortIngredients();
    this.initializeAddressAutocomplete();
    this.setupBrowserHistory();
  }

  setupTabs() {
    const tabs = this.element.querySelectorAll('[data-tab]');
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const tabType = tab.dataset.tab;
        this.switchTab(tabType);
      });
    });
  }

  switchTab(tabType) {
    this.activeTab = tabType;
    
    Object.values(this.youtubePlayers).forEach(player => {
      if (player && typeof player.pauseVideo === 'function') {
        player.pauseVideo();
      }
    });
    
    const videoWrappers = this.element.querySelectorAll('[data-video-wrapper]');
    videoWrappers.forEach(wrapper => {
      wrapper.classList.remove('playing');
    });
    
    const tabs = this.element.querySelectorAll('[data-tab]');
    const contents = this.element.querySelectorAll('[data-tab-content]');
    
    tabs.forEach(tab => {
      if (tab.dataset.tab === tabType) {
        tab.classList.add('product-stepper__tab--active');
      } else {
        tab.classList.remove('product-stepper__tab--active');
      }
    });

    contents.forEach(content => {
      if (content.dataset.tabContent === tabType) {
        content.classList.add('product-stepper__tab-content--active');
      } else {
        content.classList.remove('product-stepper__tab-content--active');
      }
    });

    const practitionerFields = this.element.querySelector('[data-practitioner-fields]');
    if (practitionerFields) {
      if (tabType === 'practitioner') {
        practitionerFields.style.display = 'block';
        this.setFieldsRequired(practitionerFields, true);
      } else {
        practitionerFields.style.display = 'none';
        this.setFieldsRequired(practitionerFields, false);
      }
    }
  }

  setFieldsRequired(container, required) {
    const inputs = container.querySelectorAll('input, textarea');
    inputs.forEach(input => {
      if (required) {
        input.setAttribute('required', '');
      } else {
        input.removeAttribute('required');
      }
    });
  }

  setupStepNavigation() {
    const nextButtons = this.element.querySelectorAll('[data-next-step]');
    nextButtons.forEach(button => {
      button.addEventListener('click', () => {
        if (this.currentStep === 1) {
          this.goToStep(2);
        }
      });
    });

    const backButtons = this.element.querySelectorAll('[data-back-step]');
    backButtons.forEach(button => {
      button.addEventListener('click', () => {
        if (this.currentStep > 1) {
          this.goToStep(this.currentStep - 1);
        }
      });
    });
  }

  goToStep(stepNumber, updateHistory = true) {
    if (!this.canAccessStep(stepNumber)) {
      this.goToStep(1, true);
      return;
    }

    this.currentStep = stepNumber;
    this.completedSteps.add(stepNumber);
    
    const stepContainers = this.element.querySelectorAll('.product-stepper__step');
    stepContainers.forEach((step, index) => {
      if (index + 1 === stepNumber) {
        step.classList.add('product-stepper__step--active');
      } else {
        step.classList.remove('product-stepper__step--active');
      }
    });

    const indicatorsContainer = this.element.querySelector('[data-step-indicators]');
    if (indicatorsContainer) {
      if (stepNumber === 1) {
        indicatorsContainer.classList.remove('product-stepper__indicators--visible');
      } else {
        indicatorsContainer.classList.add('product-stepper__indicators--visible');
      }
    }

    const indicators = this.element.querySelectorAll('.product-stepper__indicator');
    indicators.forEach((indicator, index) => {
      if (stepNumber === 1) {
        if (index === 0) {
          indicator.classList.add('product-stepper__indicator--active');
        } else {
          indicator.classList.remove('product-stepper__indicator--active');
        }
      } else {
        const activeIndex = stepNumber - 2;
        if (index === activeIndex) {
          indicator.classList.add('product-stepper__indicator--active');
        } else {
          indicator.classList.remove('product-stepper__indicator--active');
        }
      }
    });

    if (stepNumber === 3) {
      setTimeout(() => this.restoreIngredientState(), 50);
    }

    if (updateHistory) {
      const url = new URL(window.location.href);
      url.searchParams.set('step', stepNumber);
      if (this.activeTab) {
        url.searchParams.set('tab', this.activeTab);
      }
      window.history.pushState({ step: stepNumber, tab: this.activeTab }, '', url);
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  canAccessStep(stepNumber) {
    if (stepNumber === 1) return true;
    return this.completedSteps.has(stepNumber - 1);
  }

  setupForm() {
    const form = this.element.querySelector('[data-stepper-form]');
    if (!form) return;

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      
      if (this.validateForm(form)) {
        this.formData = {
          patientName: form.querySelector('#patient-name').value,
          patientPhone: form.querySelector('#patient-phone').value,
          patientAddress: form.querySelector('#patient-address').value,
          patientDob: form.querySelector('#patient-dob').value,
          patientMedical: form.querySelector('#patient-medical').value,
          tabType: this.activeTab
        };

        if (this.activeTab === 'practitioner') {
          this.formData.practitionerName = form.querySelector('#practitioner-name').value;
          this.formData.practitionerRegistration = form.querySelector('#practitioner-registration').value;
          this.formData.practitionerPhone = form.querySelector('#practitioner-phone').value;
        }
        
        this.goToStep(3);
      }
    });

    const inputs = form.querySelectorAll('input, textarea');
    inputs.forEach(input => {
      input.addEventListener('blur', () => {
        this.validateField(input);
      });
    });
  }

  validateForm(form) {
    let isValid = true;
    const inputs = form.querySelectorAll('input[required], textarea[required]');
    
    inputs.forEach(input => {
      if (!this.validateField(input)) {
        isValid = false;
      }
    });

    return isValid;
  }

  validateField(field) {
    const value = field.value.trim();
    const errorElement = this.element.querySelector(`[data-error="${field.id}"]`);
    let errorMessage = '';

    if (field.hasAttribute('required') && !value) {
      errorMessage = 'This field is required';
    } else if (field.type === 'tel' && value) {
      const phoneRegex = /^[\d\s\-\+\(\)]+$/;
      if (!phoneRegex.test(value) || value.replace(/\D/g, '').length < 10) {
        errorMessage = 'Please enter a valid phone number';
      }
    } else if (field.type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        errorMessage = 'Please enter a valid email address';
      }
    }

    if (errorMessage) {
      field.classList.add('error');
      if (errorElement) {
        errorElement.textContent = errorMessage;
        errorElement.classList.add('show');
      }
      return false;
    } else {
      field.classList.remove('error');
      if (errorElement) {
        errorElement.textContent = '';
        errorElement.classList.remove('show');
      }
      return true;
    }
  }

  setupStep3() {
    const searchInput = this.element.querySelector('[data-ingredient-search]');
    if (searchInput) {
      searchInput.addEventListener('input', (e) => this.filterIngredients(e.target.value));
    }

    const doseInput = this.element.querySelector('[data-dose-input]');
    if (doseInput) {
      doseInput.addEventListener('change', () => this.updateDoses());
      doseInput.addEventListener('input', () => this.validateDoseInput());
    }

    const doseBtns = this.element.querySelectorAll('[data-dose-action]');
    doseBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.doseAction;
        if (action === 'plus') {
          doseInput.value = parseInt(doseInput.value) + 1;
        } else if (action === 'minus' && parseInt(doseInput.value) > 30) {
          doseInput.value = parseInt(doseInput.value) - 1;
        }
        this.updateDoses();
      });
    });

    const checkboxes = this.element.querySelectorAll('[data-ingredient-checkbox]');
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => this.handleIngredientToggle(checkbox));
    });

    const amountInputs = this.element.querySelectorAll('[data-ingredient-amount]');
    amountInputs.forEach(input => {
      input.addEventListener('input', () => this.handleAmountChange(input));
      input.addEventListener('change', () => this.handleAmountChange(input));
    });

    const addToCartBtn = this.element.querySelector('[data-add-to-cart]');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', () => this.addToCart());
    }
  }

  restoreIngredientState() {
    const doseInput = this.element.querySelector('[data-dose-input]');
    if (doseInput && doseInput.value) {
      this.numberOfDoses = parseInt(doseInput.value) || 30;
    }

    const checkboxes = this.element.querySelectorAll('[data-ingredient-checkbox]');
    checkboxes.forEach(checkbox => {
      if (checkbox.checked) {
        const item = checkbox.closest('[data-ingredient-item]');
        const amountInput = item.querySelector('[data-ingredient-amount]');
        const amount = parseFloat(amountInput.value) || 0;

        if (amount > 0) {
          this.selectedIngredients.set(checkbox.dataset.code, {
            name: checkbox.dataset.name,
            tradeName: checkbox.dataset.tradeName,
            handle: checkbox.dataset.handle,
            amount: amount / 1000,
            amountInMg: amount,
            cost: parseFloat(checkbox.dataset.cost),
            adjustment: parseFloat(checkbox.dataset.adjustment) || 1
          });
        }
      }
    });

    if (this.selectedIngredients.size > 0) {
      this.updateSelectedList();
      this.calculateTotalPrice();
    }
  }

  filterIngredients(searchTerm) {
    const items = this.element.querySelectorAll('[data-ingredient-item]');
    const term = searchTerm.toLowerCase();
    
    items.forEach(item => {
      const checkbox = item.querySelector('[data-ingredient-checkbox]');
      const name = checkbox.dataset.name.toLowerCase();
      const tradeName = checkbox.dataset.tradeName.toLowerCase();
      
      if (name.includes(term) || tradeName.includes(term)) {
        item.classList.remove('hidden');
      } else {
        item.classList.add('hidden');
      }
    });
  }

  validateDoseInput() {
    const doseInput = this.element.querySelector('[data-dose-input]');
    const errorElement = this.element.querySelector('[data-dose-error]');
    const value = parseInt(doseInput.value);

    if (value < 30) {
      errorElement.textContent = 'Minimum 30 doses required';
      errorElement.classList.add('show');
    } else {
      errorElement.textContent = '';
      errorElement.classList.remove('show');
    }
  }

  updateDoses() {
    const doseInput = this.element.querySelector('[data-dose-input]');
    this.numberOfDoses = parseInt(doseInput.value) || 30;
    this.validateDoseInput();
    this.calculateTotalPrice();
  }

  handleIngredientToggle(checkbox) {
    const item = checkbox.closest('[data-ingredient-item]');
    const amountInput = item.querySelector('[data-ingredient-amount]');
    const errorElement = item.querySelector('[data-ingredient-error]');

    if (checkbox.checked) {
      amountInput.removeAttribute('disabled');
      amountInput.focus();
      
      if (amountInput.value && parseFloat(amountInput.value) > 0) {
        this.handleAmountChange(amountInput);
      }
    } else {
      amountInput.setAttribute('disabled', '');
      amountInput.value = '';
      errorElement.classList.remove('show');
      errorElement.textContent = '';
      this.selectedIngredients.delete(checkbox.dataset.code);
      this.updateSelectedList();
      this.calculateTotalPrice();
    }
  }

  handleAmountChange(input) {
    const item = input.closest('[data-ingredient-item]');
    const checkbox = item.querySelector('[data-ingredient-checkbox]');
    const conversionSpan = item.querySelector('[data-unit-conversion]');
    const errorElement = item.querySelector('[data-ingredient-error]');
    
    const amount = parseFloat(input.value) || 0;
    const maxDose = parseFloat(checkbox.dataset.maxDose);
    const unitType = checkbox.dataset.unitType;
    const unitToMg = parseFloat(checkbox.dataset.unitToMg) || 1;

    if (unitType && unitType !== 'mg' && conversionSpan) {
      const convertedValue = amount * unitToMg;
      conversionSpan.textContent = `(${convertedValue.toFixed(2)} ${unitType})`;
    }

    if (amount > maxDose) {
      errorElement.textContent = `Cannot exceed ${maxDose}mg`;
      errorElement.classList.add('show');
    } else {
      errorElement.textContent = '';
      errorElement.classList.remove('show');
    }

    if (checkbox.checked && amount > 0) {
      this.selectedIngredients.set(checkbox.dataset.code, {
        name: checkbox.dataset.name,
        tradeName: checkbox.dataset.tradeName,
        handle: checkbox.dataset.handle,
        amount: amount / 1000,
        amountInMg: amount,
        cost: parseFloat(checkbox.dataset.cost),
        adjustment: parseFloat(checkbox.dataset.adjustment) || 1
      });
      this.updateSelectedList();
      this.calculateTotalPrice();
    } else if (this.selectedIngredients.has(checkbox.dataset.code)) {
      this.selectedIngredients.delete(checkbox.dataset.code);
      this.updateSelectedList();
      this.calculateTotalPrice();
    }
  }

  updateSelectedList() {
    const selectedList = this.element.querySelector('[data-selected-list]');
    if (!selectedList) return;

    if (this.selectedIngredients.size === 0) {
      selectedList.innerHTML = '<p class="product-stepper__no-selection">No ingredients selected</p>';
      return;
    }

    let html = '';
    this.selectedIngredients.forEach((ingredient, code) => {
      html += `
        <div class="product-stepper__selected-item">
          <span class="product-stepper__selected-name">${ingredient.name}</span>
          <span class="product-stepper__selected-dose">${ingredient.amountInMg.toFixed(2)}mg</span>
        </div>
      `;
    });

    selectedList.innerHTML = html;
  }

  calculateTotalPrice() {
    let totalIngredientCost = 0;
    let totalIngredientWeight = 0;

    this.selectedIngredients.forEach((ingredient) => {
      totalIngredientWeight += ingredient.amount;
      const ingredientCost = ingredient.amount * ingredient.cost * ingredient.adjustment;
      totalIngredientCost += ingredientCost;
    });

    let basePowderWeight = totalIngredientWeight;
    let totalIngredientAndPowderWeight = totalIngredientWeight + basePowderWeight;

    if (totalIngredientWeight > 0 && totalIngredientAndPowderWeight < 10) {
      basePowderWeight = 10 - totalIngredientWeight;
    } else if (totalIngredientWeight > 0) {
      if (totalIngredientAndPowderWeight % 5 !== 0) {
        basePowderWeight += (5 - (totalIngredientAndPowderWeight % 5));
      }
    }

    const basePowderCost = basePowderWeight * this.PRICING_CONSTANTS.BASE_POWDER_COST_PER_GRAM;
    const rawCostPerDose = (totalIngredientCost + basePowderCost) * 2;
    const markedUpCost = rawCostPerDose * this.numberOfDoses;
    
    let totalPrice = markedUpCost;
    if (markedUpCost > 0) {
      totalPrice = markedUpCost + this.PRICING_CONSTANTS.PACKAGING_COST + this.PRICING_CONSTANTS.LABOR_COST;
    }

    const priceElement = this.element.querySelector('[data-total-price]');
    if (priceElement) {
      priceElement.textContent = `$${totalPrice.toFixed(2)}`;
    }

    return totalPrice;
  }

  showCartError(message) {
    const errorElement = this.element.querySelector('[data-cart-error]');
    const errorMessage = this.element.querySelector('[data-cart-error-message]');
    
    if (errorElement && errorMessage) {
      errorMessage.textContent = message;
      errorElement.style.display = 'flex';
      
      errorElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  hideCartError() {
    const errorElement = this.element.querySelector('[data-cart-error]');
    if (errorElement) {
      errorElement.style.display = 'none';
    }
  }

  async addToCart() {
    const addToCartBtn = this.element.querySelector('[data-add-to-cart]');
    
    this.hideCartError();
    
    if (this.selectedIngredients.size === 0) {
      this.showCartError('Please select at least one ingredient');
      return;
    }

    const hasErrors = Array.from(this.element.querySelectorAll('[data-ingredient-error].show')).length > 0;
    if (hasErrors) {
      this.showCartError('Please fix ingredient dose errors before adding to cart');
      return;
    }

    if (this.numberOfDoses < 30) {
      this.showCartError('Minimum 30 doses required');
      return;
    }

    try {
      addToCartBtn.disabled = true;
      addToCartBtn.textContent = 'Adding...';

      const totalPrice = this.calculateTotalPrice();
      const formulation = Array.from(this.selectedIngredients.values())
        .map(ing => `${ing.handle}::${ing.amount}::${ing.name}`)
        .join('||');

      const properties = {
        '_name': this.formData.patientName,
        '_phone': this.formData.patientPhone,
        '_address': this.formData.patientAddress,
        '_dob': this.formData.patientDob,
        '_medicalConditions': this.formData.patientMedical,
        '_formulation': formulation,
        '_totalPrice': `$${totalPrice.toFixed(2)}`,
        '_numberOfDoses': this.numberOfDoses,
        '_tabType': this.formData.tabType
      };

      if (this.formData.tabType === 'practitioner') {
        properties['_practitioner'] = this.formData.practitionerName;
        properties['_practitionerRegNo'] = this.formData.practitionerRegistration;
        properties['_practitionerContact'] = this.formData.practitionerPhone;
      }

      const formData = {
        items: [{
          id: this.variantId,
          quantity: 1,
          properties: properties
        }]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const cart = await fetch('/cart.js').then(res => res.json());
      
      const checkoutEndpoint = 'https://script.google.com/macros/s/AKfycbxaIn-S977yhynbKISmfbgzkZrx4rcaHM-FsK7PyvmSWoJ-Uv9p7CyV597gPQ0BTKnb/exec';
      const checkoutResponse = await fetch(checkoutEndpoint, {
        redirect: "follow",
        method: "POST",
        body: JSON.stringify({ token: cart.token }),
        headers: {
          "Content-Type": "text/plain;charset=utf-8",
        },
      });

      if (!checkoutResponse.ok) {
        throw new Error('Failed to create checkout');
      }

      const data = await checkoutResponse.json();
      const invoiceUrl = data.draftOrderCreate?.draftOrder?.invoiceUrl;
      
      if (invoiceUrl) {
        window.location.href = invoiceUrl;
      } else {
        throw new Error('No invoice URL received from checkout');
      }
    } catch (error) {
      console.error('Error:', error);
      this.showCartError('Failed to add to cart. Please try again.');
    } finally {
      addToCartBtn.disabled = false;
      addToCartBtn.textContent = 'Add To Cart';
    }
  }

  setupVideo() {
    if (typeof YT === 'undefined' || typeof YT.Player === 'undefined') {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      
      window.onYouTubeIframeAPIReady = () => {
        this.initializeYouTubePlayers();
      };
    } else {
      this.initializeYouTubePlayers();
    }
  }

  initializeYouTubePlayers() {
    const videoWrappers = this.element.querySelectorAll('[data-video-wrapper]');
    
    videoWrappers.forEach((wrapper, index) => {
      const playerElement = wrapper.querySelector('[data-video-player]');
      const playButton = wrapper.querySelector('[data-video-play]');
      const videoId = playerElement?.dataset.videoId;
      
      if (!videoId || !playerElement) return;
      
      const playerId = 'player-' + Math.random().toString(36).substr(2, 9);
      playerElement.id = playerId;
      
      const player = new YT.Player(playerId, {
        videoId: videoId,
        playerVars: {
          autoplay: 0,
          controls: 1,
          modestbranding: 1,
          rel: 0,
          showinfo: 0
        },
        events: {
          onStateChange: (event) => {
            if (event.data === YT.PlayerState.PLAYING) {
              wrapper.classList.add('playing');
            } else if (event.data === YT.PlayerState.PAUSED || event.data === YT.PlayerState.ENDED) {
              wrapper.classList.remove('playing');
            }
          }
        }
      });
      
      this.youtubePlayers[playerId] = player;
      
      if (playButton) {
        playButton.addEventListener('click', () => {
          wrapper.classList.add('playing');
          player.playVideo();
        });
      }
    });
  }

  setupBrowserHistory() {
    const urlParams = new URLSearchParams(window.location.search);
    const stepParam = urlParams.get('step');
    const tabParam = urlParams.get('tab');

    if (tabParam && (tabParam === 'patient' || tabParam === 'practitioner')) {
      this.switchTab(tabParam);
    }

    if (stepParam) {
      const step = parseInt(stepParam);
      if (step >= 1 && step <= 3 && this.canAccessStep(step)) {
        this.goToStep(step, false);
      } else {
        this.goToStep(1, true);
      }
    } else {
      const url = new URL(window.location.href);
      url.searchParams.set('step', this.currentStep);
      if (this.activeTab) {
        url.searchParams.set('tab', this.activeTab);
      }
      window.history.replaceState({ step: this.currentStep, tab: this.activeTab }, '', url);
    }

    window.addEventListener('popstate', (event) => {
      if (event.state && event.state.step) {
        const requestedStep = event.state.step;
        
        if (this.canAccessStep(requestedStep)) {
          this.goToStep(requestedStep, false);
          
          if (event.state.tab && event.state.tab !== this.activeTab) {
            this.switchTab(event.state.tab);
          }
        } else {
          this.goToStep(1, true);
        }
      } else {
        const urlParams = new URLSearchParams(window.location.search);
        const step = parseInt(urlParams.get('step')) || 1;
        const tab = urlParams.get('tab') || 'patient';
        
        if (this.canAccessStep(step)) {
          this.goToStep(step, false);
          if (tab !== this.activeTab) {
            this.switchTab(tab);
          }
        } else {
          this.goToStep(1, true);
        }
      }
    });
  }

  sortIngredients() {
    const medicineList = this.element.querySelector('[data-medicine-list]');
    if (!medicineList) return;

    const items = Array.from(medicineList.querySelectorAll('[data-ingredient-item]'));
    
    items.sort((a, b) => {
      const nameA = a.querySelector('[data-ingredient-checkbox]').dataset.name;
      const nameB = b.querySelector('[data-ingredient-checkbox]').dataset.name;
      return nameA.localeCompare(nameB);
    });

    items.forEach(item => medicineList.appendChild(item));
  }

  initializeAddressAutocomplete() {
    if (typeof google === 'undefined' || typeof google.maps === 'undefined') {
      setTimeout(() => this.initializeAddressAutocomplete(), 100);
      return;
    }

    const addressInput = this.element.querySelector('#patient-address');
    if (!addressInput) return;

    const countryCode = window.STEPPER_COUNTRY_CODE || 'US';
    
    const autocomplete = new google.maps.places.Autocomplete(addressInput, {
      componentRestrictions: { country: countryCode },
      fields: ['address_components', 'formatted_address'],
      types: ['address']
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place.formatted_address) {
        return;
      }

      this.validateAddress(place.formatted_address);
    });
  }

  async validateAddress(address) {
    const apiUrl = "https://content-addressvalidation.googleapis.com/v1:validateAddress?alt=json&key=AlzaSyBKjbapJ1C0pRdl3zwZOSKOOPUhnVZSFew";
    const addressInput = this.element.querySelector('#patient-address');
    const addressField = addressInput?.closest('.product-stepper__form-field');
    
    if (!addressInput) return false;

    const countryCode = window.STEPPER_COUNTRY_CODE || 'US';
    
    const payload = {
      address: {
        regionCode: countryCode,
        addressLines: [address],
      },
    };

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      
      if (data?.result?.verdict?.hasUnconfirmedComponents && 
          data?.result?.verdict?.inputGranularity == "OTHER" && 
          !data?.result?.verdict?.addressComplete) {
        if (addressField) addressField.classList.add("error");
        return false;
      } else {
        if (addressField) addressField.classList.remove("error");
        return true;
      }
    } catch (error) {
      if (addressField) addressField.classList.add("error");
      return false;
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const steppers = document.querySelectorAll('.product-stepper');
  steppers.forEach(stepper => new ProductStepper(stepper));
});